import { useEffect, useRef, useState } from 'react';
import type { KnowledgeEntry, KnowledgeJoinKeep, KnowledgeJoinResponse } from '@ni/shared';
import { useMayWrite } from '../../api/app-context';
import { axisOfWrite } from '../../api/client';
import { useJoinKnowledge, useJoinPreview, useKnowledge } from '../../api/hooks';
import { errorLine, isNotFound, validationSentence } from '../../api/errors';
import { Modal } from '../ui/modal';
import { WorkingButton } from '../ui/working-button';
import { AccessRefusedModal } from './rename-modal';
import { SEARCH_DEBOUNCE_MS, useSettled } from '../../lib/debounce';
import { isKnowledgeSearch } from '../../lib/knowledge';
import { JOIN_SEARCH_KINDS, joinCandidates, joinKeepDefault } from '../../lib/knowledge-join';
import {
  CANCEL,
  KNOWLEDGE_JOIN_CHANGE,
  KNOWLEDGE_JOIN_GONE,
  KNOWLEDGE_JOIN_HELP,
  KNOWLEDGE_JOIN_KEEP_ASK,
  KNOWLEDGE_JOIN_NONE_MATCH,
  KNOWLEDGE_JOIN_PICKER_NOTE,
  KNOWLEDGE_JOIN_SEARCHING,
  KNOWLEDGE_JOIN_SEARCH_LABEL,
  KNOWLEDGE_JOIN_SEARCH_PLACEHOLDER,
  KNOWLEDGE_JOIN_TITLE,
  KNOWLEDGE_JOIN_TRACKED_MARK,
  KNOWLEDGE_JOIN_TYPE_MORE,
  MERGE_CANDIDATE_CONFIRM_ACTION,
  MERGE_CANDIDATE_CONFIRM_LINE,
  MERGE_CANDIDATE_CONFIRM_WORKING,
  knowledgeConnections,
  knowledgeJoinChosen,
  knowledgeJoinLead,
} from '../../lib/copy';
import { EmptyLine } from '../ui/empty-line';
import { Button } from '../ui/button';

/** The record whose entry this door was opened from — one half of every choice. */
export interface JoinHere {
  entity_id: string;
  display_name: string;
  /** Whether the office keeps a record of this person. Decides the default. */
  tracked: boolean;
}

/**
 * **Join with another record** (organisations-as-subjects note §2 chunk 1,
 * "Join by hand") — the by-hand door for a fold the system never asks about.
 *
 * Two steps and no free text: search the people already on file, pick one, say
 * which of the two survives. There is no third step about the name, because
 * there is nothing to ask: the record that survives keeps what it is called, and
 * the other one's spelling stays behind as a redirect — which is the half of the
 * act nobody can see and the whole reason it is safe to do here.
 *
 * **A picker rather than a box**, for `link-modal.tsx`'s reason: a name typed
 * here would be a second door for minting a record, and the fold would then be
 * with whatever the typing resolved to.
 *
 * **The choice is by name, twice, and never by id.** That is not only a rule
 * about what the office reads — it is what the wire carries. The body says
 * `'this'` or `'other'`, so a modal that rendered the right names against the
 * wrong ids could not fold the wrong record even in principle.
 *
 * The view is split from the writer the way `RenameRecordModalView` is, and for
 * the same reason: this package's suite has no DOM, so what is on the page is
 * asserted against the view's markup.
 */
export function JoinRecordModalView({
  mayWrite,
  here,
  results,
  typed,
  searching,
  chosen,
  keep,
  readback,
  working,
  error,
  joined,
  onTyped,
  onChoose,
  onClear,
  onPickKeep,
  onSave,
  onClose,
}: {
  /**
   * Whether this reader may join two records. **The gate is here**, on the write,
   * rather than on the control that opens this modal — see `AccessRefusedModal`
   * (`rename-modal.tsx`) for why the write and not the entry point.
   */
  mayWrite: boolean;
  here: JoinHere;
  /** What the search came back with, already stripped of `here` itself. */
  results: readonly KnowledgeEntry[];
  typed: string;
  searching: boolean;
  /** The record picked out of the results, or null while the search is open. */
  chosen: KnowledgeEntry | null;
  /** Which of the two survives. Filled in from {@link joinKeepDefault} on the pick. */
  keep: KnowledgeJoinKeep;
  /**
   * **What this join would do**, in the server's own words and for the direction
   * `keep` names (fold-tracked-people note §7) — rendered verbatim, composed
   * nowhere on this side.
   *
   * Null is every absence there is and they are all the same absence: nothing
   * picked yet, the read still in flight, the read refused, or a server that
   * has nothing to say because one of the two records has gone. None of them is
   * a thing to print, and none of them stops the join.
   */
  readback: string | null;
  working: boolean;
  error: unknown;
  /** Set once the join has landed: the server's own sentence about it. */
  joined: string | null;
  onTyped: (next: string) => void;
  onChoose: (entry: KnowledgeEntry) => void;
  onClear: () => void;
  onPickKeep: (next: KnowledgeJoinKeep) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  /* **Two refusals this door reads for itself.** A 404 is the record picked out
     of the search having been folded or removed between the picking and the
     pressing, and on this door the likeliest cause is somebody else doing the
     very thing the reader was about to — which is what the line says, because
     the envelope's bare "Not found" is a fragment.

     Everything else is the server's own sentence, and deliberately: `MERGE_REFUSED`
     names in the office's words why these two cannot be joined here (one of them
     carries research, or both do), and `NAME_BUSY` says to try again. Neither is
     a thing this file could say better, and `errorLine` renders them as written. */
  const line = isNotFound(error)
    ? KNOWLEDGE_JOIN_GONE
    : (validationSentence(error) ?? errorLine(error));

  /* The two sides of the choice, in the order the office met them: the record
     they were standing on, then the one they went and found. `keep`'s two words
     are exactly these two rows, which is why the radio needs no id at all. */
  const sides: { value: KnowledgeJoinKeep; name: string; tracked: boolean }[] =
    chosen === null
      ? []
      : [
          { value: 'this', name: here.display_name, tracked: here.tracked },
          { value: 'other', name: chosen.display_name, tracked: chosen.tracked !== null },
        ];

  if (!mayWrite) return <AccessRefusedModal label={KNOWLEDGE_JOIN_TITLE} onClose={onClose} />;

  return (
    <Modal label={KNOWLEDGE_JOIN_TITLE} onClose={onClose}>
      <div className="modal-head">{KNOWLEDGE_JOIN_TITLE}</div>
      <div className="modal-line">{knowledgeJoinLead(here.display_name)}</div>

      <div className="link-modal">
        {/* **When this door is the right one**, said before the search rather
            than after the press: the reader's first question is why there is a
            button for something the system is supposed to notice on its own. */}
        <div className="field-note">{KNOWLEDGE_JOIN_HELP}</div>

        {chosen === null ? (
          <>
            <div className="field search-field">
              <label className="label" htmlFor="knowledge-join-q">
                {KNOWLEDGE_JOIN_SEARCH_LABEL}
              </label>
              <input
                id="knowledge-join-q"
                type="search"
                autoComplete="off"
                placeholder={KNOWLEDGE_JOIN_SEARCH_PLACEHOLDER}
                value={typed}
                onChange={(event) => onTyped(event.target.value)}
              />
            </div>
            {/* Said under the box rather than in the placeholder: it is a rule
                about what this door does, not a hint about what to type. */}
            <div className="src">{KNOWLEDGE_JOIN_PICKER_NOTE}</div>

            {!isKnowledgeSearch(typed) ? (
              <EmptyLine>{KNOWLEDGE_JOIN_TYPE_MORE}</EmptyLine>
            ) : searching ? (
              <EmptyLine>{KNOWLEDGE_JOIN_SEARCHING}</EmptyLine>
            ) : results.length === 0 ? (
              <EmptyLine>{KNOWLEDGE_JOIN_NONE_MATCH}</EmptyLine>
            ) : (
              <div className="link-results">
                {results.map((entry) => (
                  <button
                    key={entry.entity_id}
                    type="button"
                    className="knowledge-touch"
                    onClick={() => onChoose(entry)}
                  >
                    <span className="knowledge-touch-name">{entry.display_name}</span>
                    {/* The connection count is the one thing on a bare name that
                        tells two people apart at a glance — how much of the
                        record hangs off this entry — and "(tracked)" says the
                        other, which decides which way the fold can run. */}
                    <span className="src">
                      {[
                        entry.tracked !== null ? KNOWLEDGE_JOIN_TRACKED_MARK : null,
                        knowledgeConnections(entry.connections),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="modal-line">{knowledgeJoinChosen(chosen.display_name)}</div>
            <button type="button" className="quiet-action" onClick={onClear} disabled={working}>
              {KNOWLEDGE_JOIN_CHANGE}
            </button>

            {/* **Which of the two the office is keeping** — radios, not pills,
                for the merge card's reason: this is one choice out of two with
                one of them already made, which is what a radio group is, and it
                has to read as a setting on the join rather than as a third
                button beside "Join them".

                By name only. "(tracked)" beside one of them is the single thing
                a reader cannot see from the names and the one that decides the
                answer: a tracked record cannot be folded away, so the other
                choice is a refusal waiting to happen. */}
            <div className="modal-choice">
              <div className="choice-ask">{KNOWLEDGE_JOIN_KEEP_ASK}</div>
              {/* The group carries the question as its name, as the merge card's
                  does: without it a screen reader reaches two radios called
                  "Clint" and "Clint Phillips" with nothing saying what is being
                  asked about them. */}
              <div role="radiogroup" aria-label={KNOWLEDGE_JOIN_KEEP_ASK}>
                {sides.map((side) => (
                  <label className="picker-option" key={side.value}>
                    <input
                      type="radio"
                      name="knowledge-join-keep"
                      value={side.value}
                      checked={keep === side.value}
                      disabled={working}
                      onChange={() => onPickKeep(side.value)}
                    />
                    <span>
                      {side.tracked ? `${side.name} ${KNOWLEDGE_JOIN_TRACKED_MARK}` : side.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* **What this join would actually do**, server-owned and verbatim,
                under the choice that decides it (fold-tracked-people note §7).

                Here rather than anywhere else on the modal because it is the
                consequence of the radio directly above: a fold now unbinds the
                losing side's identity, supersedes its brief and changes which id
                the record lives at, and which of those happens to whom is
                exactly what `keep` says. It moves when the radio moves, so it is
                announced.

                Nothing at all while there is nothing to say — in flight,
                refused, or a record gone since the page loaded. No placeholder
                and no sentence of ours: this side composes none of it, and a
                line invented here to fill the gap would be a promise nobody
                made. */}
            {readback ? (
              <div className="relate-preview" aria-live="polite">
                <div className="relate-sentence">{readback}</div>
              </div>
            ) : null}

            {/* The last thing read before a press with no undo, in the same
                words the merge card uses for the same act. Above the button and
                not beside it: it is what the button means, not a note about it. */}
            <div className="modal-line">{MERGE_CANDIDATE_CONFIRM_LINE}</div>
          </>
        )}

        {/* What the record now says, in the server's own words — it is the
            answer that knows which entry survived, so it is the answer that says
            so. Shown where the reader is looking; the writer closes the modal a
            beat later. */}
        {joined ? (
          <div className="link-done" aria-live="polite">
            <div className="src">{joined}</div>
          </div>
        ) : null}

        {error ? <div className="src">{line}</div> : null}
      </div>

      <div className="modal-actions">
        <WorkingButton
          label={MERGE_CANDIDATE_CONFIRM_ACTION}
          workingLabel={MERGE_CANDIDATE_CONFIRM_WORKING}
          working={working}
          /* Not pressable once it has landed: the modal stands for a beat to say
             the sentence, and a second press in that window would ask the server
             to fold a record that is already a redirect — a 404 in place of the
             answer the reader is looking at. */
          disabled={chosen === null || joined !== null}
          onClick={onSave}
        />
        {/* Never disabled while the write runs, as every other confirm in the
            app: walking away is the reader's to do. */}
        <Button variant="act" onClick={onClose}>
          {CANCEL}
        </Button>
      </div>
    </Modal>
  );
}

/** How long the sentence stands before the modal closes itself. */
const CLOSE_AFTER_MS = 900;

/**
 * The writer: the search, the pick, the choice and the one mutation.
 *
 * On success the sentence is shown and *then* the modal closes, rather than
 * closing on the response: the sentence's second clause — "The old entry still
 * finds it." — is the only place the redirect is ever mentioned, and a modal
 * that vanished on the press would never have said it. `onJoined` is handed the
 * whole answer at the same moment, because the caller may be standing on the
 * record that just went away and needs the survivor's id to point itself at.
 */
export function JoinRecordModal({
  here,
  onClose,
  onJoined,
}: {
  here: JoinHere;
  onClose: () => void;
  onJoined: (response: KnowledgeJoinResponse) => void;
}) {
  const mayWrite = useMayWrite(axisOfWrite('joinKnowledge'));
  const [typed, setTyped] = useState('');
  const [settled, pushTyped] = useSettled('', SEARCH_DEBOUNCE_MS);
  const [chosen, setChosen] = useState<KnowledgeEntry | null>(null);
  const [keep, setKeep] = useState<KnowledgeJoinKeep>('this');
  const [joined, setJoined] = useState<string | null>(null);

  const answer = useKnowledge({ q: settled, kinds: JOIN_SEARCH_KINDS });
  const write = useJoinKnowledge(here.entity_id);

  /* **What the join would do, for the direction the office picked**
     (fold-tracked-people note §7). Asked only once both halves are known — the
     other record, and which of the two survives — because those two are the
     whole of what the sentence is about.

     The key carries `keep`, so flipping the radio is a *different* read rather
     than a refetch of the same one. That is what keeps the other direction's
     sentence off the screen while this one is in flight: a key never asked has
     no data, `preview.data` is undefined, and the modal shows nothing rather
     than a promise about the fold the office just changed their mind about.
     A refusal lands in the same place for the same reason. */
  const preview = useJoinPreview(here.entity_id, chosen?.entity_id ?? null, keep);

  /* **The closing timer, cancelled if the reader gets there first.** The modal
     stands for a beat after the answer so the sentence can be read, and `onClose`
     is the caller's — on the record page it repoints the page at the survivor.
     A reader who closes the modal inside that window (Escape, the backdrop,
     Cancel) would otherwise have it fire against an unmounted modal a moment
     later and move the page under them. */
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  /* **The default follows the pick**, because it is a fact about the pair and
     there is no pair until one is made. Re-run on every change of the chosen
     record — including a reader going back and picking a different one, where
     leaving the previous answer standing would be a choice nobody made about two
     records they are no longer looking at. */
  useEffect(() => {
    if (chosen === null) return;
    setKeep(
      joinKeepDefault(here, {
        display_name: chosen.display_name,
        tracked: chosen.tracked !== null,
      }),
    );
    // The pick is what this is about; `here` is fixed for the life of the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen]);

  /* A search below the minimum asks for nothing (`useKnowledge` sends no `q` and
     the server answers with the most connected), so the results are the entries
     and nothing else — never the opening lists, which are not what was asked
     for. */
  const entries = isKnowledgeSearch(settled) ? (answer.data?.entries ?? []) : [];

  return (
    <JoinRecordModalView
      mayWrite={mayWrite}
      here={here}
      results={joinCandidates(entries, here.entity_id)}
      typed={typed}
      searching={isKnowledgeSearch(settled) && answer.isPending}
      chosen={chosen}
      keep={keep}
      /* The sentence the server wrote for this direction, and null for every
         other state there is — not yet asked, in flight, refused, or answered
         with nothing because a record has gone. Nothing here ever fills that
         gap: the office reads the server's words or reads none. */
      readback={preview.data?.readback ?? null}
      working={write.isPending}
      error={write.error}
      joined={joined}
      onTyped={(next) => {
        setTyped(next);
        pushTyped(next);
      }}
      onChoose={(entry) => {
        if (write.isError) write.reset();
        setJoined(null);
        setChosen(entry);
      }}
      onClear={() => {
        if (write.isError) write.reset();
        setJoined(null);
        setChosen(null);
      }}
      onPickKeep={(next) => {
        if (write.isError) write.reset();
        setKeep(next);
      }}
      onSave={() => {
        if (chosen === null || write.isPending || joined !== null) return;
        write.mutate(
          { other_entity_id: chosen.entity_id, keep },
          {
            onSuccess: (response) => {
              setJoined(response.sentence);
              onJoined(response);
              closeTimer.current = setTimeout(onClose, CLOSE_AFTER_MS);
            },
          },
        );
      }}
      onClose={onClose}
    />
  );
}
